import { Clock, Instagram, MapPin } from 'lucide-react';
import { Link } from 'react-router';

import { BUSINESS_HOURS, CONTACT, SITE } from '@/config/site';
import { FOOTER_LINKS } from '@/data/navigation';
import { WA_LINKS } from '@/lib/whatsapp';

import { Container } from '../common/Container';
import { PawIcon, WhatsAppIcon } from '../common/icons';
import { Logo } from '../common/Logo';
import { Separator } from '../ui/misc';

export function Footer() {
  return (
    <footer className="bg-paw-pattern bg-brown-950 text-cream-300">
      <Container className="py-14">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          <div className="flex flex-col gap-4">
            <div className="w-fit rounded-2xl bg-cream-100 px-3 py-2">
              <Logo static className="h-12" />
            </div>

            <p className="text-sm leading-relaxed">
              Distribuidora de rações para cães e gatos. Marcas líderes em nutrição, atendimento
              humano e entrega em {SITE.serviceArea}.
            </p>

            <p className="flex items-center gap-2 text-xs text-caramel-500">
              <PawIcon className="size-3.5" />
              Desde {SITE.foundedYear}
            </p>
          </div>

          <FooterColumn title="Catálogo" links={FOOTER_LINKS.catalogo} />
          <FooterColumn title="Institucional" links={FOOTER_LINKS.institucional} />

          <div className="flex flex-col gap-4">
            <h3 className="font-display text-sm font-bold tracking-wider text-cream-100 uppercase">
              Atendimento
            </h3>

            <a
              href={WA_LINKS.general}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-fit items-center gap-2 rounded-xl bg-whats-500 px-4 py-3 font-display text-sm font-bold text-white transition-colors hover:bg-whats-600"
            >
              <WhatsAppIcon className="size-4" />
              {CONTACT.whatsappDisplay}
            </a>

            <a
              href={CONTACT.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm transition-colors hover:text-cream-100"
            >
              <Instagram className="size-4 shrink-0 text-caramel-500" aria-hidden="true" />@
              {CONTACT.instagramHandle}
            </a>

            <p className="flex items-start gap-2 text-sm">
              <MapPin className="mt-0.5 size-4 shrink-0 text-caramel-500" aria-hidden="true" />
              {SITE.serviceArea}
            </p>

            <div className="flex items-start gap-2 text-sm">
              <Clock className="mt-0.5 size-4 shrink-0 text-caramel-500" aria-hidden="true" />
              <ul className="flex flex-col gap-0.5">
                {BUSINESS_HOURS.map((slot) => (
                  <li key={slot.days}>
                    <span className="font-semibold text-cream-100">{slot.days}:</span> {slot.hours}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <Separator className="my-10 bg-brown-800" />

        <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
          <p className="text-center text-xs sm:text-left">
            © {new Date().getFullYear()} {SITE.legalName}. Todos os direitos reservados.
          </p>

          <p className="text-center text-xs text-cream-300/70 sm:text-right">
            Preços e disponibilidade sujeitos a confirmação pelo WhatsApp.
          </p>
        </div>

        <p className="mt-6 text-center text-[11px] text-cream-300/70">
          Feito com <span className="text-orange-500">♥</span> por{' '}
          <a
            href="https://onsightx.com"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold transition-colors hover:text-caramel-500"
          >
            OnsightX
          </a>{' '}
          — inovação e qualidade.
        </p>
      </Container>

      {/* Space so the floating WhatsApp button never covers the last line. */}
      <div className="h-4 lg:h-0" />
    </footer>
  );
}

interface FooterColumnProps {
  title: string;
  links: ReadonlyArray<{ label: string; to: string }>;
}

function FooterColumn({ title, links }: FooterColumnProps) {
  return (
    <nav aria-label={title} className="flex flex-col gap-4">
      <h3 className="font-display text-sm font-bold tracking-wider text-cream-100 uppercase">
        {title}
      </h3>
      <ul className="flex flex-col gap-2.5">
        {links.map((link) => (
          <li key={link.to}>
            <Link
              to={link.to}
              className="-my-1 inline-block py-1 text-sm transition-colors hover:text-orange-400"
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}
